"use client";
import { useEffect, useState, type DragEvent as ReactDragEvent, type ReactNode } from "react";
import Link from "@/lib/spa/Link";
import { usePathname, useRouter } from "@/lib/spa/navigation";
import { Logo } from "@/components/ui/Logo";
import { DataVersionPill } from "@/components/layout/DataVersionPill";
import { ProfileSwitcher } from "@/components/layout/ProfileSwitcher";
import { ProfileAutoDetect } from "@/components/layout/ProfileAutoDetect";
import { FlowRail } from "@/components/layout/FlowRail";
import { MobileNavDrawer } from "@/components/layout/MobileNavDrawer";
import { MobileTabBar } from "@/components/layout/MobileTabBar";
import { useCompactViewport } from "@/lib/display/compactViewport";
import { useNavConfig, hrefOf, isUtilityItem, PROTECTED_IDS, type NavItem } from "@/lib/nav/navConfig";

/** An item's OWN route (null for a group, which only opens its first child). */
function ownHref(it: NavItem): string | null {
  if (it.kind === "workspace" && it.slug) return `/p/${it.slug}`;
  return it.href ?? null;
}

/**
 * Drag payload for reordering nav tabs in place. Carries `parentId\0itemId`:
 * the parent scopes the move to ONE level (a top-level tab can't be dropped
 * into a sub-tab row, matching the Settings editor's `sameLevel` rule), and the
 * id travels in the payload rather than in React state — `dragstart`'s setState
 * hasn't re-rendered when the first dragover/drop closure runs. A custom MIME
 * keeps an unrelated drag entering the bar from reading as a reorder.
 */
const DND_MIME = "application/x-mpp-navitem";
/** NUL — cannot occur in a route or a generated nav id, so it can never
 *  collide with either half of the payload. Written as an escape: a literal
 *  NUL byte in source is invisible in diffs and upsets some tooling. */
const SEP = "\u0000";

export function TopNav() {
  const pathname = usePathname();
  const router = useRouter();
  const items = useNavConfig((s) => s.items);
  const style = useNavConfig((s) => s.style);
  const hide = useNavConfig((s) => s.hide);
  const update = useNavConfig((s) => s.update);
  const hydrate = useNavConfig((s) => s.hydrate);
  useEffect(() => hydrate(), [hydrate]);

  // Warm the deferred chart chunk (~2MB incl. the indicator catalog) once,
  // at browser idle, so the first navigation into a chart-bearing page
  // doesn't pay the download+parse during the transition. Pages that embed
  // a chart on mount (Dashboard) load it anyway — this only moves the cost
  // off the navigation critical path.
  useEffect(() => {
    const warm = () =>
      void import("@/components/chart/advanced/AdvancedChartAppPane").catch(() => {});
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback;
    const t = setTimeout(() => (idle ? idle(warm, { timeout: 10_000 }) : warm()), 3_000);
    return () => clearTimeout(t);
  }, []);

  // Drag-to-reorder state. Declared before the early return below so the hook
  // order stays stable across the login/features render path.
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  useCompactViewport();
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // The bar's real height, as `--mpp-topnav-h`, for what is pinned under it
  // (a section's tab bar, the Holdings range bar). `--mpp-nav-h` is the one-row
  // height and stays the page-height budget (`--mpp-chrome-h`), so a
  // window-sized arrangement is drawn as before; but where the window is too
  // narrow for the tabs the bar wraps to two rows, and a tab bar pinned at the
  // one-row height sat half under it while the page scrolled (2026-10-14).
  // The safe-area padding is left out: the pinned bars add `--mpp-sat`.
  useEffect(() => {
    const bar = document.getElementById("mpp-topnav");
    if (!bar || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const read = () => {
      const pad = Number.parseFloat(getComputedStyle(bar).paddingTop) || 0;
      const h = Math.round(bar.getBoundingClientRect().height - pad);
      if (h > 0) root.style.setProperty("--mpp-topnav-h", `${h}px`);
    };
    read();
    const watch = new ResizeObserver(read);
    watch.observe(bar);
    return () => {
      watch.disconnect();
      root.style.removeProperty("--mpp-topnav-h");
    };
  }, [pathname]);

  // Public pages (login + the marketing features page) have no app chrome.
  if (pathname === "/login" || pathname === "/features") return null;

  const topLevel = items.filter((i) => i.visible);

  // Flatten every routable (top-level OR child) target, tagged with its
  // top-level ancestor, so longest-prefix matching picks the single active
  // leaf — and from it, the active top-level tab + active sub-tab.
  const flat: Array<{ href: string; topId: string; childId?: string }> = [];
  for (const top of topLevel) {
    const th = ownHref(top);
    if (th) flat.push({ href: th, topId: top.id });
    for (const child of top.children ?? []) {
      if (!child.visible) continue;
      const ch = ownHref(child);
      if (ch) flat.push({ href: ch, topId: top.id, childId: child.id });
    }
  }
  const matches = (href: string) => pathname === href || pathname.startsWith(`${href}/`);
  const activeLeaf = flat
    .filter((f) => matches(f.href))
    .reduce<(typeof flat)[number] | null>((best, f) => (!best || f.href.length > best.href.length ? f : best), null);
  const activeTopId = activeLeaf?.topId ?? "";
  const activeChildId = activeLeaf?.childId ?? "";

  const activeTop = topLevel.find((t) => t.id === activeTopId);
  const subTabs = (activeTop?.children ?? []).filter((c) => c.visible);

  const handleSignOut = async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    router.push("/login");
    router.refresh();
  };

  const linkClass = (active: boolean) =>
    `rounded-md px-2.5 py-1 text-[13px] font-medium whitespace-nowrap transition ${
      active ? "bg-accent text-on-accent shadow-sm" : "text-ink-muted hover:bg-accent-soft hover:text-accent-ink"
    }`;

  const renderTarget = (it: NavItem, className: string, children: ReactNode) => {
    const target = hrefOf(it);
    if (it.kind === "link" && it.external && target) {
      return (
        <a key={it.id} href={target} target="_blank" rel="noopener noreferrer" className={className}>
          {children}
        </a>
      );
    }
    return (
      <Link key={it.id} href={target ?? "#"} prefetch className={className}>
        {children}
      </Link>
    );
  };

  const clearDnd = () => {
    setDragId(null);
    setOverId(null);
  };

  /**
   * Move `srcId` to `dstId`'s slot. Same-level only: a top-level tab reorders
   * among top-level tabs, a sub-tab among its own siblings. Dropping across
   * levels is ignored rather than re-parented — re-parenting is a deliberate
   * act, and the Settings editor is where you do it.
   */
  const reorder = (srcParent: string, srcId: string, dstParent: string, dstId: string) => {
    if (srcParent !== dstParent || srcId === dstId) return clearDnd();
    const next = JSON.parse(JSON.stringify(items)) as NavItem[];
    const list = srcParent ? next.find((t) => t.id === srcParent)?.children : next;
    if (!list) return clearDnd();
    const from = list.findIndex((x) => x.id === srcId);
    const to = list.findIndex((x) => x.id === dstId);
    if (from < 0 || to < 0) return clearDnd();
    list.splice(to, 0, ...list.splice(from, 1));
    update(next);
    clearDnd();
  };

  /** Drag handlers for one nav pill. `parentId` is "" for a top-level tab. */
  const dnd = (it: NavItem, parentId: string) => ({
    draggable: true,
    onDragStart: (e: ReactDragEvent) => {
      setDragId(it.id);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData(DND_MIME, `${parentId}${SEP}${it.id}`);
      // A nav pill wraps an <a>, which natively drags its URL; blank the text
      // payload so a stray drop can't turn a reorder into a navigation.
      e.dataTransfer.setData("text/plain", "");
    },
    onDragOver: (e: ReactDragEvent) => {
      if (!e.dataTransfer.types.includes(DND_MIME)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setOverId(it.id);
    },
    onDrop: (e: ReactDragEvent) => {
      if (!e.dataTransfer.types.includes(DND_MIME)) return;
      e.preventDefault();
      const [srcParent = "", srcId = ""] = (e.dataTransfer.getData(DND_MIME) || "").split(SEP);
      reorder(srcParent, srcId, parentId, it.id);
    },
    onDragEnd: clearDnd,
  });

  // Wrap a nav item so it can be DRAGGED to reorder, and so a small × appears
  // on hover — a one-click "hide from nav" (it's not deleted, just
  // visible:false; restore in Settings → Pages). Protected items (Settings)
  // still drag; they just don't get the ×.
  const withHide = (it: NavItem, node: ReactNode, parentId = "") => {
    const dndCls = `${dragId === it.id ? "opacity-40" : ""} ${
      overId === it.id && dragId && dragId !== it.id ? "ring-1 ring-inset ring-accent rounded-md" : ""
    }`;
    if (PROTECTED_IDS.has(it.id)) {
      return (
        <span key={it.id} {...dnd(it, parentId)} className={`relative inline-flex ${dndCls}`}>
          {node}
        </span>
      );
    }
    return (
      <span key={it.id} {...dnd(it, parentId)} className={`group/pill relative inline-flex ${dndCls}`}>
        {node}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            hide(it.id);
          }}
          title={`Hide "${it.label}" — restore in Settings → Pages`}
          aria-label={`Hide ${it.label}`}
          className="absolute -right-1 -top-1 hidden h-4 w-4 place-items-center rounded-full border border-line bg-surface text-ink-faint shadow-sm transition group-hover/pill:grid hover:border-bear hover:bg-bear-soft hover:text-bear"
        >
          <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </span>
    );
  };

  // The sections sit after the logo; Settings and Help sit at the right, beside
  // the account controls (`isUtilityItem`). Both are the same list, so a drag
  // still reorders, and the Settings editor still arranges, the whole bar.
  const sectionTabs = topLevel.filter((t) => !isUtilityItem(t));
  const utilityTabs = topLevel.filter(isUtilityItem);

  /** One top-level tab. In dropdown style a parent with children reveals them
   *  on hover, the menu opening toward the middle of the bar. */
  const topTab = (p: NavItem, side: "left" | "right") => {
    const kids = (p.children ?? []).filter((c) => c.visible);
    const active = p.id === activeTopId;
    if (style === "dropdown" && kids.length) {
      return (
        <div key={p.id} className="group relative">
          {withHide(
            p,
            renderTarget(
              p,
              linkClass(active),
              <span className="inline-flex items-center gap-1">
                {p.label}
                <svg className="h-3 w-3 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
              </span>,
            ),
          )}
          <div className={`absolute ${side === "left" ? "left-0" : "right-0"} top-full z-50 hidden min-w-[13rem] pt-1.5 group-hover:block`}>
            <div className="flex flex-col gap-0.5 rounded-lg border border-line bg-surface p-1 shadow-panel-raised">
              {kids.map((c) => withHide(c, renderTarget(c, `${linkClass(c.id === activeChildId)} text-left`, c.label), p.id))}
            </div>
          </div>
        </div>
      );
    }
    return withHide(p, renderTarget(p, linkClass(active), p.label));
  };

  const accountBtn = (compactHit: boolean) => (
    <Link
      href="/account/password"
      title="Account"
      aria-label="Account"
      className={`grid shrink-0 place-items-center rounded-md border border-line text-ink-muted transition hover:border-line-strong hover:bg-surface-sunken hover:text-ink ${
        pathname.startsWith("/account") ? "border-accent text-accent" : ""
      } ${compactHit ? "h-11 w-11" : "h-8 w-8"}`}
    >
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    </Link>
  );

  const signOutBtn = (compactHit: boolean) => (
    <button
      onClick={handleSignOut}
      title="Sign out"
      aria-label="Sign out"
      className={`grid shrink-0 place-items-center rounded-md border border-line text-ink-muted transition hover:border-line-strong hover:bg-surface-sunken hover:text-ink ${
        compactHit ? "h-11 w-11" : "h-8 w-8"
      }`}
    >
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
      </svg>
    </button>
  );

  return (
    <>
      <header
        id="mpp-topnav"
        className="sticky top-0 z-40 border-b-2 border-accent bg-surface/95 shadow-toolbar backdrop-blur"
        style={{ paddingTop: "var(--mpp-sat)", paddingLeft: "var(--mpp-sal)", paddingRight: "var(--mpp-sar)" }}
      >
        {/* Compact chrome — iPhone / iPad. CSS-gated so a late hook cannot
            flash the desktop bar. Hover menus become the drawer. */}
        <div className="mpp-compact-only flex w-full items-center gap-2 px-2 py-1.5">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-md text-ink transition hover:bg-surface-sunken"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <Link href="/dashboard" prefetch aria-label="Afiat MarketPulse Pro home" className="shrink-0 text-ink">
            <Logo size={26} />
          </Link>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] font-semibold leading-tight text-ink">
              {activeTop?.label ?? "MarketPulse"}
            </div>
            {activeChildId ? (
              <div className="truncate text-[11px] text-ink-muted">
                {subTabs.find((c) => c.id === activeChildId)?.label}
              </div>
            ) : null}
          </div>
          <div id="mpp-page-toolbar" className="mpp-h-scroll max-w-[40%] items-center gap-1 empty:hidden" />
          <DataVersionPill compact />
          {accountBtn(true)}
          {signOutBtn(true)}
        </div>

        <div className="mpp-desktop-only flex w-full items-center justify-between gap-3 px-4 py-2 xl:px-6 2xl:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Link href="/dashboard" prefetch aria-label="Afiat MarketPulse Pro home" className="shrink-0 text-ink">
              <Logo size={26} />
            </Link>
            <nav className="flex flex-wrap items-center gap-0.5">
              {sectionTabs.map((p) => topTab(p, "left"))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {/* Page-level workspace controls (Add panel / Lock / Reset) portal in
                here from the current page's PaneWorkspace, so they live in the top
                bar next to the session pill instead of floating over the panel.
                Empty (hidden) on pages without a workspace. Compact chrome owns
                the same id on the other row — only one of these is displayed. */}
            <div id="mpp-page-toolbar-desktop" className="flex items-center gap-1.5 empty:hidden" />
            <FlowRail />
            <ProfileSwitcher />
            <ProfileAutoDetect />
            <DataVersionPill />
            {utilityTabs.length > 0 && (
              <nav aria-label="Settings and help" className="flex items-center gap-0.5">
                {utilityTabs.map((p) => topTab(p, "right"))}
              </nav>
            )}
            {accountBtn(false)}
            {signOutBtn(false)}
          </div>
        </div>

        {/* Second-row sub-tabs — the active top-level page's nested sub-pages.
            (Only in "subtabs" style; "dropdown" shows children in a hover menu.)
            Compact: a single horizontally-scrolling chip row, 44px taps. */}
        {style === "subtabs" && subTabs.length > 0 && (
          <nav className="mpp-subtabs mpp-h-scroll items-center gap-0.5 border-t border-line px-2 py-1 md:flex-wrap md:overflow-visible md:px-6 md:py-1.5 xl:px-8 2xl:px-10">
            {subTabs.map((c) => withHide(c, renderTarget(c, `${linkClass(c.id === activeChildId)} min-h-11 px-3 text-sm md:min-h-0 md:px-2.5 md:py-1 md:text-xs`, c.label), activeTopId))}
          </nav>
        )}
      </header>

      <MobileNavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        items={topLevel}
        activeTopId={activeTopId}
        activeChildId={activeChildId}
        headerExtra={
          <div className="flex flex-col gap-2">
            <FlowRail />
            <div className="flex flex-wrap items-center gap-2">
              <ProfileSwitcher />
              <ProfileAutoDetect />
            </div>
          </div>
        }
        footer={
          <div className="flex flex-col gap-1">
            <Link
              href="/account/password"
              onClick={() => setDrawerOpen(false)}
              className="flex min-h-11 w-full items-center gap-2 rounded-lg px-3 text-[15px] font-medium text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Account
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="flex min-h-11 w-full items-center gap-2 rounded-lg px-3 text-[15px] font-medium text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        }
      />
      <MobileTabBar pathname={pathname} items={topLevel} onMore={() => setDrawerOpen(true)} />
    </>
  );
}
